import { debounce } from "@excalidraw/common";
import type { MaybePromise } from "@excalidraw/common/utility-types";
import { saveSceneToFile } from "@excalidraw/excalidraw/data";
import type { JSONExportData } from "@excalidraw/excalidraw/data/json";
import { getFileHandleType } from "@excalidraw/excalidraw/data/blob";

const DEFAULT_AUTO_SAVE_DELAY = 1000;

export const createSceneFileAutoSaver = (opts?: {
  debounceMs?: number;
  onError?: (error: unknown) => void;
}) => {
  let latestPayload:
    | {
        data: MaybePromise<JSONExportData>;
        filename: string;
        sourceFileHandle: FileSystemFileHandle | null;
      }
    | null = null;
  let saveInFlight = false;
  let rerunRequested = false;
  let pendingTimer = false;
  let drainPromise: Promise<void> | null = null;
  let resolveDrainPromise: (() => void) | null = null;
  let lastSaveResult = false;

  const ensureDrainPromise = () => {
    if (!drainPromise) {
      drainPromise = new Promise<void>((resolve) => {
        resolveDrainPromise = resolve;
      });
    }
    return drainPromise;
  };

  const resolveDrainIfIdle = () => {
    if (saveInFlight || rerunRequested || pendingTimer) {
      return;
    }

    const resolve = resolveDrainPromise;
    drainPromise = null;
    resolveDrainPromise = null;
    resolve?.();
  };

  const scheduleRun = debounce(
    () => {
      pendingTimer = false;
      return run();
    },
    opts?.debounceMs ?? DEFAULT_AUTO_SAVE_DELAY,
  );

  const run = async () => {
    if (saveInFlight) {
      rerunRequested = true;
      ensureDrainPromise();
      return false;
    }

    const payload = latestPayload;
    if (!payload?.sourceFileHandle) {
      lastSaveResult = false;
      resolveDrainIfIdle();
      return false;
    }

    if (getFileHandleType(payload.sourceFileHandle) !== "onepaper") {
      lastSaveResult = false;
      resolveDrainIfIdle();
      return false;
    }

    saveInFlight = true;
    ensureDrainPromise();

    try {
      await saveSceneToFile({
        data: payload.data,
        filename: payload.filename,
        sourceFileHandle: payload.sourceFileHandle,
        fileHandle: payload.sourceFileHandle,
      });
      lastSaveResult = true;
      return true;
    } catch (error) {
      lastSaveResult = false;
      (opts?.onError ?? console.error)(error);
      return false;
    } finally {
      saveInFlight = false;

      if (rerunRequested) {
        rerunRequested = false;
        pendingTimer = true;
        scheduleRun();
      } else {
        resolveDrainIfIdle();
      }
    }
  };

  return {
    schedule(payload: NonNullable<typeof latestPayload>) {
      if (!payload.sourceFileHandle) {
        latestPayload = null;
        pendingTimer = false;
        lastSaveResult = false;
        scheduleRun.cancel();
        resolveDrainIfIdle();
        return;
      }

      latestPayload = payload;
      pendingTimer = true;
      ensureDrainPromise();
      scheduleRun();
    },
    flush() {
      const result = scheduleRun.flush();

      if (!saveInFlight && !rerunRequested && !pendingTimer) {
        return Promise.resolve(result).then(() => true);
      }

      ensureDrainPromise();
      return Promise.resolve(result)
        .then(() => drainPromise ?? undefined)
        .then(() => lastSaveResult);
    },
    cancel() {
      latestPayload = null;
      rerunRequested = false;
      saveInFlight = false;
      pendingTimer = false;
      lastSaveResult = false;
      scheduleRun.cancel();
      resolveDrainIfIdle();
    },
  };
};
